/**
 * Admin — manage one user: change role (MASTER_ADMIN only), reset password,
 * deactivate/reactivate, each behind a confirm sheet.
 * Wired: GET /api/admin/users/<id> (list fallback), PATCH /api/admin/users/<id>,
 * POST /api/admin/users/<id>/reset-password. Port of screens-admin.jsx
 * AdminUserDetail.
 */
import { useLocalSearchParams } from 'expo-router';
import React from 'react';
import { View } from 'react-native';

import { formatINR, space } from '@bero/tokens';
import { bool, extractItems, str } from '@/components/gate/api';
import { InfoNote } from '@/components/gate/bits';
import { AdminScreenShell, goBack, SmallButton } from '@/features/account-admin/bits';
import { Icon } from '@/features/account-admin/icons';
import { api } from '@/lib/client';
import { useSession } from '@/lib/session';
import { useTheme } from '@/theme';
import { AppText, Avatar, Button, EmptyState, Hairline, ListRow, Pill, Screen, Sheet, Skeleton } from '@/ui';

type ManagedUser = { id: string; name: string; flat: string; role: string; residentType: string; active: boolean; membershipId: string | null; membershipStatus: string };

type Eligibility = {
  memberClass: string; isDefaulter: boolean; overdueAmount: number;
  canVote: boolean; canContest: boolean; canBookAmenity: boolean;
  reasons: { gate: string; citation: string; text: string }[];
};

const MEMBER_CLASSES: [value: string, label: string, sub: string][] = [
  ['PRIMARY', 'Primary member', 'Owner on the share certificate'],
  ['ASSOCIATE', 'Associate member', 'Family admitted with the member\u2019s consent'],
  ['JOINT', 'Joint member', 'Co-owner; first-named votes'],
  ['PROVISIONAL', 'Provisional member', 'Awaiting confirmation \u2014 no vote yet'],
  ['NOMINAL', 'Nominal member', 'Tenant / licensee \u2014 no vote, no contest'],
];

const ROLES: Array<[value: string, label: string]> = [
  ['RESIDENT', 'Resident'],
  ['SUB_ADMIN', 'Sub admin'],
  ['MASTER_ADMIN', 'Master admin'],
];

function toUser(raw: unknown, id: string): ManagedUser | null {
  const name = str(raw, 'name', 'fullName');
  if (!name) return null;
  return {
    id: str(raw, 'id', 'userId') ?? id,
    name,
    flat: str(raw, 'flat', 'flatCode') ?? '',
    role: (str(raw, 'role') ?? 'RESIDENT').toUpperCase(),
    residentType: (str(raw, 'residentType', 'type') ?? '').toUpperCase(),
    active: bool(raw, 'active', 'isActive') ?? true,
    membershipId: str((raw as { membership?: unknown })?.membership, 'id') ?? null,
    membershipStatus: (str((raw as { membership?: unknown })?.membership, 'status') ?? '').toUpperCase(),
  };
}

function subtitle(u: ManagedUser): string {
  const role = u.role === 'MASTER_ADMIN' ? 'Master admin'
    : u.role === 'SUB_ADMIN' ? 'Sub admin'
      : u.residentType.charAt(0) + u.residentType.slice(1).toLowerCase();
  return [u.flat, role].filter(Boolean).join(' · ');
}

export default function AdminUserDetailScreen() {
  const { colors } = useTheme();
  const { user: me } = useSession();
  const { id } = useLocalSearchParams<{ id: string }>();
  const userId = typeof id === 'string' ? id : '';

  const [target, setTarget] = React.useState<ManagedUser | null>(null);
  const [state, setState] = React.useState<'loading' | 'ready' | 'error'>('loading');
  const [sheet, setSheet] = React.useState<'role' | 'class' | 'reset' | 'deactivate' | 'membership' | null>(null);
  const [pendingRole, setPendingRole] = React.useState<string>('RESIDENT');
  const [busy, setBusy] = React.useState(false);
  const [notice, setNotice] = React.useState<string | null>(null);
  const [elig, setElig] = React.useState<Eligibility | null>(null);
  const [memberClass, setMemberClass] = React.useState<string>('PRIMARY');

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      let u: ManagedUser | null = null;
      try {
        const body = await api.get<unknown>(`/api/admin/users/${userId}`);
        const raw = ((body && typeof body === 'object' && (body as Record<string, unknown>).user) || body) as Record<string, unknown>;
        u = toUser(raw, userId);
        if (!cancelled && raw && typeof raw === 'object') {
          if (raw.eligibility) setElig(raw.eligibility as Eligibility);
          if (typeof raw.memberClass === 'string') setMemberClass(raw.memberClass);
        }
      } catch { /* fall through to list */ }
      if (!u) {
        try {
          const list = extractItems<unknown>(await api.get<unknown>('/api/admin/users'), 'users');
          const hit = list.find((x) => (str(x, 'id', 'userId') ?? '') === userId);
          if (hit) u = toUser(hit, userId);
        } catch { /* handled below */ }
      }
      if (cancelled) return;
      setTarget(u);
      setPendingRole(u?.role ?? 'RESIDENT');
      setState(u ? 'ready' : 'error');
    })();
    return () => { cancelled = true; };
  }, [userId]);

  const changeRole = async () => {
    if (!target || pendingRole === target.role) { setSheet(null); return; }
    setBusy(true);
    try {
      await api.patch<unknown>(`/api/admin/users/${target.id}`, { role: pendingRole });
      setTarget({ ...target, role: pendingRole });
      setNotice(`Role updated to ${ROLES.find(([v]) => v === pendingRole)?.[1] ?? pendingRole}.`);
      setSheet(null);
    } catch {
      setNotice('Could not change the role. Try again.');
      setSheet(null);
    } finally {
      setBusy(false);
    }
  };

  const changeClass = async (value: string) => {
    if (!target || busy) return;
    setBusy(true);
    try {
      await api.patch<unknown>(`/api/admin/users/${target.id}`, { memberClass: value });
      setMemberClass(value);
      const body = await api.get<{ user?: { eligibility?: Eligibility } }>(`/api/admin/users/${target.id}`);
      if (body?.user?.eligibility) setElig(body.user.eligibility);
      setNotice(`Member class updated to ${MEMBER_CLASSES.find(([v]) => v === value)?.[1] ?? value}.`);
      setSheet(null);
    } catch (e) {
      setNotice((e as { error?: string })?.error ?? 'Could not update the member class.');
      setSheet(null);
    } finally {
      setBusy(false);
    }
  };

  const resetPassword = async () => {
    if (!target) return;
    setBusy(true);
    try {
      const body = await api.post<unknown>(`/api/admin/users/${target.id}/reset-password`);
      const temp = str(body, 'tempPassword', 'temporaryPassword', 'password');
      setNotice(temp
        ? `Temporary password: ${temp} — share it privately.`
        : `Password reset sent to ${target.name}.`);
      setSheet(null);
    } catch {
      setNotice('Could not reset the password. Try again.');
      setSheet(null);
    } finally {
      setBusy(false);
    }
  };

  const setActive = async (next: boolean) => {
    if (!target) return;
    setBusy(true);
    try {
      const body = await api.patch<unknown>(`/api/admin/users/${target.id}`, { isActive: next });
      if ((body as { needsApproval?: boolean })?.needsApproval) {
        setNotice('Sent for a second approval — track it under Pending actions.');
        setSheet(null);
        return;
      }
      setTarget({ ...target, active: next });
      setNotice(next ? 'Account reactivated.' : 'Account deactivated.');
      setSheet(null);
    } catch {
      setNotice(next ? 'Could not reactivate the account.' : 'Could not deactivate the account.');
      setSheet(null);
    } finally {
      setBusy(false);
    }
  };

  const actOnMembership = async () => {
    if (!target?.membershipId) return;
    const removing = target.membershipStatus === 'APPROVED';
    setBusy(true);
    try {
      await api.post<unknown>(`/api/admin/members/${target.membershipId}`, { action: removing ? 'remove' : 'reapprove' });
      setTarget({ ...target, membershipStatus: removing ? 'REJECTED' : 'APPROVED' });
      setNotice(removing ? 'Removed from the society — their access here ends now.' : 'Membership re-approved.');
    } catch (e) {
      setNotice((e as { error?: string })?.error ?? 'Could not update the membership.');
    } finally {
      setBusy(false);
      setSheet(null);
    }
  };

  return (
    <AdminScreenShell title="Manage user">
      {state === 'loading' ? (
        <View style={{ paddingHorizontal: space.s4, paddingTop: space.s4, gap: space.s3, alignItems: 'center' }}>
          <Skeleton w={72} h={72} r={36} />
          <Skeleton w="55%" h={20} />
          <Skeleton w="40%" h={14} />
        </View>
      ) : state === 'error' || !target ? (
        <EmptyState
          icon={<Icon name="user" size={40} color={colors.textTertiary} />}
          title="User unavailable"
          body="We could not load this user right now. Try again in a bit."
        />
      ) : (
        <Screen padded style={{ paddingTop: space.s4 }}>
          <View style={{ alignItems: 'center' }}>
            <Avatar name={target.name} size={72} />
            <AppText variant="headingLg" style={{ marginTop: space.s3 }}>{target.name}</AppText>
            <AppText variant="bodyMd" tone="tertiary" style={{ marginTop: space.s1 }}>{subtitle(target)}</AppText>
            {!target.active ? <View style={{ marginTop: space.s2 }}><Pill status="rejected" label="DEACTIVATED" /></View> : null}
          </View>

          {notice ? <InfoNote icon="info" style={{ marginTop: space.s4 }}>{notice}</InfoNote> : null}

          <View style={{ marginTop: space.s5 }}>
            <AppText variant="label" tone="tertiary" style={{ marginBottom: space.s2 }}>Membership</AppText>
            <ListRow
              left={<Icon name="users" size={20} color={colors.textSecondary} />}
              title="Member class"
              subtitle={MEMBER_CLASSES.find(([v]) => v === memberClass)?.[1] ?? memberClass}
              onPress={() => setSheet('class')}
            />
            {elig ? (
              <View style={{
                borderWidth: 1, borderColor: colors.borderSoft, borderRadius: 16,
                padding: space.s4, marginTop: space.s3,
              }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s2 }}>
                  <AppText variant="label" tone="tertiary" style={{ flex: 1 }}>Eligibility</AppText>
                  {elig.isDefaulter ? <Pill status="rejected" label="DEFAULTER" /> : null}
                </View>
                {([['Vote in polls', elig.canVote], ['Contest elections', elig.canContest],
                   ['Book amenities', elig.canBookAmenity]] as [string, boolean][]).map(([label, ok]) => (
                  <View key={label} style={{ flexDirection: 'row', alignItems: 'center', gap: space.s2, marginTop: space.s2 }}>
                    <Icon name={ok ? 'check' : 'x'} size={16} color={ok ? colors.primary700 : colors.danger500} />
                    <AppText variant="bodyMd" style={{ flex: 1 }}>{label}</AppText>
                  </View>
                ))}
                {elig.isDefaulter ? (
                  <AppText variant="bodySm" tone="tertiary" style={{ marginTop: space.s2 }}>
                    {formatINR(elig.overdueAmount)} unpaid beyond the 3-month window.
                  </AppText>
                ) : null}
                {elig.reasons.map((r) => (
                  <View key={r.text} style={{ flexDirection: 'row', gap: 6, marginTop: space.s2, alignItems: 'flex-start' }}>
                    <Icon name="lock" size={12} color={colors.accent700} />
                    <AppText variant="bodySm" style={{ color: colors.accent700, flex: 1 }}>
                      {r.text}{r.citation ? ` ${r.citation}` : ''}
                    </AppText>
                  </View>
                ))}
              </View>
            ) : null}
          </View>

          <View style={{ marginTop: space.s5 }}>
            {me?.role === 'MASTER_ADMIN' ? (
              <>
                <ListRow
                  left={<Icon name="shield" size={20} color={colors.textSecondary} />}
                  title="Change role"
                  subtitle={ROLES.find(([v]) => v === target.role)?.[1] ?? target.role}
                  onPress={() => { setPendingRole(target.role); setSheet('role'); }}
                />
                <Hairline inset={48} />
              </>
            ) : null}
            <ListRow
              left={<Icon name="lock" size={20} color={colors.textSecondary} />}
              title="Reset password"
              subtitle="Issue a fresh password for this account"
              onPress={() => setSheet('reset')}
            />
            <Hairline inset={48} />
            {target.membershipId && target.membershipStatus === 'APPROVED' ? (
              <>
                <ListRow
                  left={<Icon name="x" size={20} color={colors.danger500} />}
                  title="Remove from society"
                  subtitle="Ends this member's access to this society"
                  onPress={() => setSheet('membership')}
                />
                <Hairline inset={48} />
              </>
            ) : target.membershipId && target.membershipStatus === 'REJECTED' ? (
              <>
                <ListRow
                  left={<Icon name="user-plus" size={20} color={colors.textSecondary} />}
                  title="Re-approve member"
                  subtitle="Restore this member's society access"
                  onPress={() => setSheet('membership')}
                />
                <Hairline inset={48} />
              </>
            ) : null}
            {target.active ? (
              <ListRow
                left={<Icon name="x" size={20} color={colors.danger500} />}
                title="Deactivate account"
                subtitle="Removes access to notices, payments and the gate"
                onPress={() => setSheet('deactivate')}
              />
            ) : (
              <ListRow
                left={<Icon name="check" size={20} color={colors.textSecondary} />}
                title="Reactivate account"
                subtitle="Restore this resident's access"
                onPress={() => setSheet('deactivate')}
              />
            )}
          </View>
        </Screen>
      )}

      {/* Remove / re-approve society membership */}
      <Sheet visible={sheet === 'membership'} onClose={() => setSheet(null)}>
        {target ? (
          <View>
            <AppText variant="headingSm" weight="600">
              {target.membershipStatus === 'APPROVED' ? `Remove ${target.name}?` : `Re-approve ${target.name}?`}
            </AppText>
            <AppText variant="bodySm" tone="secondary" style={{ marginTop: space.s2 }}>
              {target.membershipStatus === 'APPROVED'
                ? 'They lose access to this society immediately — gate, notices, payments, everything. You can re-approve them later from this screen.'
                : 'Their society access is restored immediately and they will be told by email.'}
            </AppText>
            <Button
              title={target.membershipStatus === 'APPROVED' ? 'Remove from society' : 'Re-approve member'}
              kind={target.membershipStatus === 'APPROVED' ? 'destructive' : undefined}
              loading={busy}
              style={{ marginTop: space.s4 }}
              onPress={() => { void actOnMembership(); }}
            />
            <Button title="Cancel" kind="tertiary" style={{ marginTop: space.s1 }} onPress={() => setSheet(null)} />
          </View>
        ) : null}
      </Sheet>

      {/* Change role */}
      <Sheet visible={sheet === 'role'} onClose={() => setSheet(null)}>
        <AppText variant="headingMd" weight="500" style={{ marginBottom: space.s4 }}>Change role</AppText>
        <View style={{ gap: space.s2 }}>
          {ROLES.map(([value, label]) => (
            <SmallButton key={value} label={label} active={pendingRole === value} onPress={() => setPendingRole(value)} />
          ))}
        </View>
        <Button title="Save role" loading={busy} style={{ marginTop: space.s4 }} onPress={() => { void changeRole(); }} />
      </Sheet>

      {/* Member class */}
      <Sheet visible={sheet === 'class'} onClose={() => setSheet(null)}>
        <AppText variant="headingMd" weight="500" style={{ marginBottom: space.s2 }}>Member class</AppText>
        <AppText variant="bodySm" tone="tertiary" style={{ marginBottom: space.s4 }}>
          Drives voting, contesting and amenity rights under the MCS Act and model bye-laws.
        </AppText>
        <View style={{ gap: space.s2 }}>
          {MEMBER_CLASSES.map(([value, label, sub]) => (
            <ListRow
              key={value}
              title={label}
              subtitle={sub}
              right={memberClass === value ? <Icon name="check" size={18} color={colors.primary700} /> : undefined}
              onPress={() => { void changeClass(value); }}
            />
          ))}
        </View>
      </Sheet>

      {/* Reset password */}
      <Sheet visible={sheet === 'reset'} onClose={() => setSheet(null)}>
        <AppText variant="headingMd" weight="500">Reset password?</AppText>
        <AppText variant="bodyMd" tone="secondary" style={{ marginTop: space.s2 }}>
          {target ? `${target.name} will need the new password to sign in again.` : ''}
        </AppText>
        <Button title="Reset password" loading={busy} style={{ marginTop: space.s4 }} onPress={() => { void resetPassword(); }} />
        <Button title="Cancel" kind="tertiary" style={{ marginTop: space.s1 }} onPress={() => setSheet(null)} />
      </Sheet>

      {/* Deactivate / reactivate */}
      <Sheet visible={sheet === 'deactivate'} onClose={() => setSheet(null)}>
        <AppText variant="headingMd" weight="500">
          {target?.active ? 'Deactivate this account?' : 'Reactivate this account?'}
        </AppText>
        <AppText variant="bodyMd" tone="secondary" style={{ marginTop: space.s2 }}>
          {target?.active
            ? `${target?.name ?? 'This resident'} will immediately lose access to the app. You can reactivate later.`
            : `${target?.name ?? 'This resident'} will get their access back immediately.`}
        </AppText>
        <Button
          title={target?.active ? 'Deactivate account' : 'Reactivate account'}
          kind={target?.active ? 'destructive' : 'primary'}
          loading={busy}
          style={{ marginTop: space.s4 }}
          onPress={() => { void setActive(!(target?.active ?? true)); }}
        />
        <Button title="Cancel" kind="tertiary" style={{ marginTop: space.s1 }} onPress={() => setSheet(null)} />
      </Sheet>
    </AdminScreenShell>
  );
}
